/**
 * Заведение клиента агентства: что уходит на сервер и что экран добавляет к
 * отказу.
 *
 * Отдельно от экрана по той же причине, что и гейт оформления: здесь живут
 * решения, у которых есть правильный и неправильный ответ, а проверить их
 * глазами нельзя — оба отказа возникают только у организации, которая уже
 * упёрлась в тариф или раздала квоту до копейки. Разработчик такого состояния
 * не видит никогда.
 *
 * Два отказа, ради которых всё это написано, приходят с одним кодом ответа
 * (`409`) и означают прямо противоположные вещи.
 *
 * * `plan_limit_reached` — тариф не даёт ещё одного пространства
 *   (`services/workspaces.py`: `count_active_workspaces >= max_workspaces`).
 *   Исправляется деньгами, и выход отсюда один — экран тарифа. Отказ без
 *   ссылки туда оставляет человека гадать, что именно кончилось.
 * * `allocation_exceeds_quota` — пространство завелось бы, но выделить ему
 *   столько вопросов нельзя: сумма выделенного по организации больше купленного
 *   (`services/workspaces.py:set_allocation_on_connection`). Исправляется
 *   числом в том же поле, и человеку нужен остаток — иначе он подбирает его
 *   вслепую, повторяя отказ.
 *
 * Текст обоих отказов пишет сервер: он знает и потолок тарифа, и купленный
 * объём. Кабинет показывает его через `humanMessage` (внутри `ErrorNotice`) и
 * добавляет ровно то, чего в тексте сервера быть не может, — выход.
 */

import { ApiError } from '../api/problem';
import type { AgencyClientCreate } from '../api/team';
import { int } from '@shared/src/format';

/** Что заполнено в окне. Всё строками: поле ввода отдаёт строку. */
export interface ClientDraft {
  name: string;
  displayName: string;
  inviteEmail: string;
  tracked: string;
}

export const BLANK_CLIENT: ClientDraft = {
  name: '',
  displayName: '',
  inviteEmail: '',
  tracked: '',
};

/** Границы `AgencyClientCreate.name` из контракта. */
export const NAME_MIN = 2;
export const NAME_MAX = 200;

/** Потолок поля «Вопросов в квоту». Больше не бывает ни на одном тарифе. */
export const TRACKED_MAX = 999_999;

/**
 * Сколько вопросов вписал человек — или `null`, если это не целое число.
 *
 * Поле раньше вычищало из введённого всё, кроме цифр. Это не мягкая проверка,
 * а тихая подмена намерения: «12.5» превращалось в 125, «1e3» — в 13, и такое
 * число уезжало выделением квоты, которое забирает вопросы у остальных
 * клиентов. Ошибиться в десять раз молча страшнее, чем не принять ввод.
 *
 * Поэтому синтаксис проверяется ЦЕЛИКОМ, а не по одному знаку, и всё, что не
 * целое число, показывается человеку ошибкой поля (`trackedError`), а не
 * исправляется за него.
 */
export function trackedValue(raw: string): number | null {
  const text = raw.trim();
  if (!/^\d+$/.test(text)) return null;
  const value = Number(text);
  return value <= TRACKED_MAX ? value : null;
}

/**
 * Что не так с введённым числом вопросов. `null` — всё в порядке, в том числе
 * когда поле пустое: выделение квоты необязательно и делается позже.
 */
export function trackedError(raw: string): string | null {
  const text = raw.trim();
  if (!text) return null;
  if (!/^\d+$/.test(text)) {
    return 'Вопросы считаются штуками: впишите целое число, например 40';
  }
  if (trackedValue(text) === null) {
    return `Столько вопросов не бывает: не больше ${int(TRACKED_MAX)}`;
  }
  return null;
}

/**
 * Готова ли форма к отправке.
 *
 * Проверяется то, что проверяет контракт (длина названия), и синтаксис числа
 * вопросов — иначе форма отправила бы на сервер не то число, которое человек
 * видит в поле. Почту и величину квоты сверяет сервер: повторять здесь его
 * правила значит однажды отбить адрес, который он принял бы.
 */
export function clientDraftIsReady(draft: ClientDraft): boolean {
  const name = draft.name.trim();
  if (name.length < NAME_MIN || name.length > NAME_MAX) return false;
  return trackedError(draft.tracked) === null;
}

/**
 * Сколько квоты организации ещё не роздано.
 *
 * Считается по ВСЕМ пространствам, а не по одним клиентам агентства, и это не
 * придирка. Сервер, отбивая `allocation_exceeds_quota`, складывает **все**
 * строки выделений организации (`pg_workspaces.allocated_total`), включая
 * пространство своей команды, — а список клиентов о нём молчит. Экран,
 * считающий остаток по одному этому списку, обещает больше, чем сервер
 * выделит: человек вписывает предложенное число, получает отказ, а подсказка
 * после отказа предлагает то же самое ещё раз.
 */
export function freeQuota(
  bought: number,
  allocated: readonly (number | null | undefined)[],
): number {
  return allocated.reduce<number>((left, value) => left - (value ?? 0), bought);
}

/**
 * Пространства, о выделениях которых список клиентов молчит.
 *
 * Это внутренние пространства агентства и всё, что сервер из агентского списка
 * убрал (архивные), — их выделения он в свою сумму по-прежнему берёт.
 */
export function spacesOutsideClients(
  workspaces: readonly { id: string }[],
  clients: readonly { workspace_id: string }[],
): string[] {
  const listed = new Set(clients.map((client) => client.workspace_id));
  return workspaces.filter((space) => !listed.has(space.id)).map((space) => space.id);
}

/**
 * Черновик → тело запроса.
 *
 * Пустое необязательное поле не отправляется вовсе, а не уходит пустой строкой
 * или нулём. Разница видна на первом же клиенте: `invite_email: ''` — это
 * `422` от сервера, а `tracked_questions: 0` — заведённое выделение на ноль
 * вопросов, из-за которого экран пишет «выделено 0 из 0» там, где выделения
 * просто не делали.
 *
 * Поля `brand` здесь нет: его нет и в типе (`api/team.ts`).
 */
export function agencyClientBody(draft: ClientDraft): AgencyClientCreate {
  const body: AgencyClientCreate = { name: draft.name.trim() };
  const displayName = draft.displayName.trim();
  if (displayName) body.client_display_name = displayName;
  const inviteEmail = draft.inviteEmail.trim();
  if (inviteEmail) body.invite_email = inviteEmail;
  // Не `Number(строка)`: «12.5» стало бы числом 12.5, а «12,5» — NaN. Число
  // берётся только из того, что целиком прошло проверку синтаксиса.
  const tracked = trackedValue(draft.tracked);
  if (tracked !== null) body.tracked_questions = tracked;
  return body;
}

/** Что экран добавляет к тексту сервера. */
export type Refusal =
  /** Тариф не даёт ещё одного пространства: выход — экран тарифа. */
  | { kind: 'plan' }
  /** Выделено больше, чем свободно: человеку нужен остаток. */
  | { kind: 'quota'; free: number }
  /** Прошлая попытка всё-таки прошла: клиент уже заведён, список отстал. */
  | { kind: 'repeat' }
  /** Всё прочее, включая обрыв связи: текста сервера достаточно. */
  | { kind: 'plain' };

/**
 * Разбор отказа.
 *
 * Ветвление по машинному `code`, а не по статусу: оба разбираемых отказа
 * приходят с `409`, и различить их по коду ответа невозможно.
 */
export function refusalOf(error: unknown, free: number): Refusal {
  if (!(error instanceof ApiError)) return { kind: 'plain' };
  if (error.problem.code === 'plan_limit_reached') return { kind: 'plan' };
  if (error.problem.code === 'idempotency_key_reused') {
    // Ключ живёт всю попытку, поэтому этот код здесь означает ровно одно:
    // прошлая отправка с этим ключом дошла и завелась, а ответ до кабинета не
    // доехал (сервер записывает ответ только на успехе, на отказе ключ
    // отпускает). Клиент уже есть — человеку нужен не текст про тело запроса,
    // а обновлённый список.
    return { kind: 'repeat' };
  }
  if (error.problem.code === 'allocation_exceeds_quota') {
    // Отрицательный остаток бывает: квоту организации могли уменьшить уже
    // после того, как её раздали. «Свободно −40 вопросов» — не остаток, а
    // головоломка; свободного места в этом случае просто нет.
    return { kind: 'quota', free: Math.max(0, free) };
  }
  return { kind: 'plain' };
}
